import debounce from 'lodash/debounce';
import IntersectionObserver from 'intersection-observer-polyfill';
import { sendCustomPageEvent } from '../../../scripts/config/analytics';
import { setMaxHeightStyle } from '../../../scripts/utils/setMaxHeightStyle';
import { calcOffsetBoxesSizeWithoutHeaderOffer } from '../../../scripts/utils/offsetBoxes';
import { urlStoredFilter } from '../../../scripts/utils/urlStoredFilter';

const COLLECTIONS_SUBNAV_PARENT_ATTR = 'data-list-collections-subnav-parent';
const COLLECTIONS_SUBNAV_TOGGLE_ATTR = 'data-subnav-toggle';
const COLLECTIONS_SUBNAV_TOGGLE_TEXT_ATTR = 'data-subnav-toggle-text';

const STORED_SCROLLER_MENU_ATTR = 'data-url-stored-scroll-menu';
const STORED_SCROLLER_ANCHOR_ATTR = 'data-url-stored-scroll-anchor';
const STORED_SCROLLER_KEY_ATTR = 'data-anchor';

const COLLECTIONS_SECTION_ATTR = 'data-section';
const COLLECTIONS_OPEN_SUBNAV_ATTR = 'data-list-collections-subnav-open';
const COLLECTIONS_SUBNAV_LIST_ATTR =
  'data-list-collections-subnav-dropdown-list';
const COLLECTION_FILTER_DATA_KEY = 'anchor';

export function runCollectionsSubnav() {
  const intersectionThreshold = 0.6;
  const intersectionRatio = 0.6;
  const scrollTime = 500;

  let isEnableObserve = true;

  removeReloadOnClickNavLink(STORED_SCROLLER_MENU_ATTR);

  const urlScroller = urlStoredFilter({
    dataKey: COLLECTION_FILTER_DATA_KEY,
    menuSelector: `[${STORED_SCROLLER_MENU_ATTR}]`,
    targetSelector: `[${STORED_SCROLLER_ANCHOR_ATTR}]`,
    listeners: [handleChangeSubNav],
  });

  const paramValue = new URL(window.location).searchParams.get(
    COLLECTION_FILTER_DATA_KEY
  );

  const templateEl = document.querySelector(
    '[data-template="list-collections"]'
  );

  if (paramValue) {
    goToTarget(paramValue, true);
  }

  templateEl.setAttribute('data-show', 'true');

  function handleChangeSubNav(activeValue, { context = null } = {}) {
    if (!context) {
      goToTarget(activeValue);
      sendCustomPageEvent('Sub-navgation - Button Click');
    }

    closeSubnavDropdown();
    changeDropDownToggleText(activeValue);
  }

  initDropdownToggle();

  function goToTarget(targetValue, isInitiate = false) {
    if (!targetValue) {
      return;
    }

    isEnableObserve = false;

    const targetValueRegex = /^[\w-]+(?:_[\w-]+)*$/;
    const isValidTargetValue = targetValueRegex.test(targetValue);
    const targetElSelector = `[${STORED_SCROLLER_ANCHOR_ATTR}][${STORED_SCROLLER_KEY_ATTR}=${targetValue}]`;

    let targetEl = isValidTargetValue
      ? document.querySelector(targetElSelector)
      : null;

    const isDesktop = !window.matchMedia('(max-width: 1325px)').matches;

    if (isDesktop && targetEl) {
      targetEl = targetEl.closest(`[${COLLECTIONS_SECTION_ATTR}]`);
    }

    // NOTE: pass true because we should show only sub navigation
    const headerHeight = calcOffsetBoxesSizeWithoutHeaderOffer(true);
    const targetElOffsetTop = targetEl?.offsetTop - headerHeight || 0;

    window.scrollTo({
      top: targetElOffsetTop,
      behavior: isInitiate ? 'auto' : 'smooth',
    });

    // NOTE: disable observe on scrollToElement duration
    setTimeout(() => {
      isEnableObserve = true;
    }, scrollTime);
  }

  const observer = new IntersectionObserver(observeFn, {
    threshold: intersectionThreshold,
  });

  const scrollTargetElements = [
    ...document.querySelectorAll(`[${STORED_SCROLLER_ANCHOR_ATTR}]`),
  ].filter(Boolean);

  scrollTargetElements.forEach((targetEl) => {
    observer.observe(targetEl);
  });

  function observeFn(entries) {
    const entry = getMostVisibleIntersectEntry(entries);

    const targetValue = scrollTargetElements
      .find((targetEl) => targetEl === entry.target)
      ?.getAttribute(STORED_SCROLLER_KEY_ATTR);

    if (
      isEnableObserve &&
      targetValue &&
      entry.intersectionRatio > intersectionRatio
    ) {
      isEnableObserve = false;

      urlScroller.emit(targetValue, { context: observer });

      return debounce(() => {
        isEnableObserve = true;
      }, scrollTime)();
    }

    return null;
  }

  let isFirstDisplay = true;

  function getMostVisibleIntersectEntry(observerEntries) {
    if (!isFirstDisplay) {
      return observerEntries.pop();
    }

    isFirstDisplay = false;

    const sortedEntries = [...observerEntries].sort(
      (a, b) => a.intersectionRatio - b.intersectionRatio
    );

    return sortedEntries.pop();
  }
}

function initDropdownToggle() {
  const dropdownToggleEl = document.querySelector(
    `[${COLLECTIONS_SUBNAV_TOGGLE_ATTR}]`
  );
  const dropdownParentEl = document.querySelector(
    `[${COLLECTIONS_SUBNAV_PARENT_ATTR}]`
  );

  dropdownToggleEl.addEventListener('click', () => {
    const dropdownOpenAttr = dropdownParentEl.getAttribute(
      COLLECTIONS_OPEN_SUBNAV_ATTR
    );

    if (dropdownOpenAttr === 'true') {
      document.querySelector(
        `[${COLLECTIONS_SUBNAV_LIST_ATTR}]`
      ).style.maxHeight = '0px';
      dropdownParentEl.setAttribute(COLLECTIONS_OPEN_SUBNAV_ATTR, 'false');
    } else {
      setMaxHeightStyle(`[${COLLECTIONS_SUBNAV_LIST_ATTR}]`);
      dropdownParentEl.setAttribute(COLLECTIONS_OPEN_SUBNAV_ATTR, 'true');
    }
  });
}

function closeSubnavDropdown() {
  const dropdownParentEl = document.querySelector(
    `[${COLLECTIONS_SUBNAV_PARENT_ATTR}]`
  );

  document.querySelector(`[${COLLECTIONS_SUBNAV_LIST_ATTR}]`).style.maxHeight =
    '0px';
  dropdownParentEl.setAttribute(COLLECTIONS_OPEN_SUBNAV_ATTR, 'false');
}

function changeDropDownToggleText(activeValue) {
  const dropDownToggleTextEl = document.querySelector(
    `[${COLLECTIONS_SUBNAV_TOGGLE_TEXT_ATTR}]`
  );
  const activeNavItem = activeValue
    ? document.querySelector(
        `[${STORED_SCROLLER_MENU_ATTR}][${STORED_SCROLLER_KEY_ATTR}=${activeValue}]`
      )
    : null;

  if (activeNavItem) {
    dropDownToggleTextEl.textContent = activeNavItem.textContent;
  }
}

function removeReloadOnClickNavLink(navLinkSelector) {
  const navLinks = document.querySelectorAll(`[${navLinkSelector}]`);

  navLinks.forEach((navLink) => {
    navLink.addEventListener('click', (event) => {
      event.preventDefault();
    });
  });
}
